import assert from "assert";
import { unreachable } from "common/functions";
import { cancel, delayAnimation, Waiter } from "common/promises";
import log from "loglevel";
class StepChangedListeners {
    constructor() {
        this.listeners = [];
    }
    stepChanged(step) {
        for (const listener of this.listeners) {
            listener.stepChanged(step);
        }
    }
    add(listener) {
        this.listeners.push(listener);
    }
}
export class StateMachine {
    constructor({ button, bouncer, pixiContext, }) {
        this.step = 1;
        this.buttonWaiter = new Waiter();
        /**
        click시 실행해야할 async 함수
        state에 따라서 바뀐다.
        */
        this.onClick = null;
        /**
        click해서 바운싱 애니메이션 진행중일 때만 Promise<void>다.
        */
        this.bounceAnimation = null;
        this.listeners = new StepChangedListeners();
        this.heartAnimation = null;
        this.heartAnimationSate = { type: "idle" };
        this.nextStepCount = null;
        this.pixiContext = pixiContext;
        this.click = this.click.bind(this);
        this.bouncer = bouncer;
        button.callback = this.click;
    }
    async dispose() {
        var _a;
        (_a = this.heartAnimation) === null || _a === void 0 ? void 0 : _a.canceler.cancel();
        if (this.bounceAnimation != null) {
            await this.bounceAnimation;
        }
    }
    async start() {
        this.animateHeart();
        while (true) {
            let nextStep = 0;
            switch (this.step) {
                case 1:
                    nextStep = await this.bounce1();
                    break;
                case 2:
                    nextStep = await this.bounce2();
                    break;
                case 3:
                    nextStep = await this.bounce3();
                    break;
                case 4:
                    nextStep = await this.bounce4();
                    break;
                case 5:
                    const result = await this.bounce5();
                    if (result === "finish") {
                        return;
                    }
                    else {
                        nextStep = result;
                    }
                    break;
                default:
                    throw new Error("invalid step " + this.step);
            }
            this.step = nextStep;
            this.listeners.stepChanged(this.step);
            log.info("heart state change to", this.step);
        }
    }
    async bounce1() {
        this.onClick = async () => {
            this.heartAnimationSate = { type: "full", count: 1, speed: 1 };
        };
        try {
            const noClickFor = cancel.root(async (canceler) => {
                return await this.noClickFor(canceler, 1000);
            });
            const clickCount = cancel.root(async (canceler) => {
                return await this.clickCount(canceler, 2);
            });
            const result = await Promise.race([
                noClickFor.promise,
                clickCount.promise,
            ]);
            noClickFor.canceler.cancel();
            clickCount.canceler.cancel();
            if (result === "noClickFor") {
                return 1;
            }
            else if (result === "clickedEnough") {
                return 2;
            }
            unreachable(result);
        }
        finally {
            this.onClick = null;
        }
    }
    async bounce2() {
        this.onClick = async () => {
            this.heartAnimationSate = { type: "full", count: 1, speed: 1.3 };
        };
        try {
            const noClickFor = cancel.root(async (canceler) => {
                return await this.noClickFor(canceler, 1000);
            });
            const clickCount = cancel.root(async (canceler) => {
                return await this.clickCount(canceler, 3);
            });
            const result = await Promise.race([
                noClickFor.promise,
                clickCount.promise,
            ]);
            noClickFor.canceler.cancel();
            clickCount.canceler.cancel();
            if (result === "noClickFor") {
                return 1;
            }
            else if (result === "clickedEnough") {
                return 3;
            }
            unreachable();
        }
        finally {
            this.onClick = null;
        }
    }
    async bounce3() {
        this.onClick = async () => {
            this.heartAnimationSate = { type: "full", count: 2, speed: 1.5 };
        };
        try {
            const noClickFor = cancel.root(async (canceler) => {
                return await this.noClickFor(canceler, 1000);
            });
            const clickCount = cancel.root(async (canceler) => {
                return await this.clickCount(canceler, 6 * 2);
            });
            const result = await Promise.race([
                noClickFor.promise,
                clickCount.promise,
            ]);
            noClickFor.canceler.cancel();
            clickCount.canceler.cancel();
            if (result === "noClickFor") {
                return 2;
            }
            else if (result === "clickedEnough") {
                return 4;
            }
            unreachable();
        }
        finally {
            this.onClick = null;
        }
    }
    async bounce4() {
        this.onClick = async () => {
            this.heartAnimationSate = { type: "full", count: 2, speed: 1.8 };
        };
        try {
            const noClickFor = cancel.root(async (canceler) => {
                return await this.noClickFor(canceler, 1000);
            });
            const clickCount = cancel.root(async (canceler) => {
                return await this.clickCount(canceler, 5 * 2);
            });
            const result = await Promise.race([
                noClickFor.promise,
                clickCount.promise,
            ]);
            noClickFor.canceler.cancel();
            clickCount.canceler.cancel();
            if (result === "noClickFor") {
                return 3;
            }
            else if (result === "clickedEnough") {
                return 5;
            }
            unreachable();
        }
        finally {
            this.onClick = null;
        }
    }
    async bounce5() {
        this.onClick = async () => {
            this.heartAnimationSate = { type: "hyper", count: 1, speed: 2.3 };
        };
        try {
            const noClickFor = cancel.root(async (canceler) => {
                return await this.noClickFor(canceler, 1000);
            });
            const clickCount = cancel.root(async (canceler) => {
                return await this.clickCount(canceler, 8);
            });
            const result = await Promise.race([
                noClickFor.promise,
                clickCount.promise,
            ]);
            noClickFor.canceler.cancel();
            clickCount.canceler.cancel();
            if (result === "noClickFor") {
                return 4;
            }
            else if (result === "clickedEnough") {
                return "finish";
            }
            unreachable();
        }
        finally {
            this.onClick = null;
        }
    }
    async clickCount(canceler, goal) {
        log.info("call clickCount");
        assert(goal > 0);
        this.nextStepCount = goal;
        while (true) {
            canceler.check();
            await this.buttonWaiter.wait();
            canceler.check();
            await this.bounceAnimation;
            canceler.check();
            if (this.nextStepCount === 0) {
                this.nextStepCount = null;
                return "clickedEnough";
            }
        }
    }
    async noClickFor(canceler, millis) {
        while (true) {
            canceler.check();
            const timeOut = this.pixiContext.delay(millis).then(() => "timeOut");
            const click = this.buttonWaiter.wait().then(() => "click");
            const result = await Promise.race([timeOut, click]);
            canceler.check();
            if (result === "click") {
                await this.bounceAnimation;
                continue;
            }
            else if (result === "timeOut") {
                return "noClickFor";
            }
        }
    }
    async click() {
        if (this.onClick == null) {
            return;
        }
        if (this.bounceAnimation != null) {
            return;
        }
        this.buttonWaiter.trigger();
        this.buttonWaiter.reset();
        if (this.onClick != null) {
            this.bounceAnimation = this.onClick();
            await this.bounceAnimation;
            this.bounceAnimation = null;
        }
    }
    animateHeart() {
        this.heartAnimation = cancel.root(async (canceler) => {
            while (true) {
                while (this.heartAnimationSate.type === "idle") {
                    canceler.check();
                    await delayAnimation(100);
                }
                while (this.heartAnimationSate.type === "full") {
                    canceler.check();
                    if (this.heartAnimationSate.count === 0) {
                        this.heartAnimationSate = { type: "idle" };
                        continue;
                    }
                    if (this.heartAnimationSate.count > 0) {
                        this.heartAnimationSate.count -= 1;
                    }
                    if (this.nextStepCount != null && this.nextStepCount > 0) {
                        this.nextStepCount -= 1;
                        log.debug("nextStepCount changed", this.nextStepCount);
                    }
                    else {
                        log.debug("nextStepCount unchanged", this.nextStepCount);
                    }
                    await this.bouncer.bounceFull(this.heartAnimationSate.speed);
                    log.debug("bounceFull");
                }
                while (this.heartAnimationSate.type === "hyper") {
                    canceler.check();
                    if (this.heartAnimationSate.count === 0) {
                        this.heartAnimationSate = { type: "idle" };
                        continue;
                    }
                    if (this.heartAnimationSate.count > 0) {
                        this.heartAnimationSate.count -= 1;
                    }
                    if (this.nextStepCount != null && this.nextStepCount > 0) {
                        this.nextStepCount -= 1;
                        log.debug("nextStepCount changed", this.nextStepCount);
                    }
                    else {
                        log.debug("nextStepCount unchanged", this.nextStepCount);
                    }
                    await this.bouncer.bounceFull(this.heartAnimationSate.speed);
                    await this.bouncer.bounceHyper(this.heartAnimationSate.speed);
                    log.debug("bounceHyper");
                }
            }
        });
    }
}
//# sourceMappingURL=stateMachine.js.map